import { sharedIntegrationsAvailable } from '@mygpt/app/config/integrations';
import { Button, Group, Text, UnstyledButton } from '@mantine/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTTS, APIRobotInfo } from '@mygpt/app/apis';
import { isAdmin } from '@mygpt/app/apis/request';
import { useBotMethods } from '@mygpt/app/bot';
import { useChatStore } from '@mygpt/app/chat';
import FaqChildNodeList from '@mygpt/app/chat/components/FaqChildNodeList';
import {
  MESSAGE_SIGNIN_ICON_ARRAY,
  Loading,
  SvgRefresh,
  AgentMessage,
  Markdown,
  signatureClickAction,
  checkIsUrl,
  cleanMarkdownText,
  splitIntoSentences,
  FetchQuestionOptions,
  useAudioManager,
  FaqNodeSearchByIds,
  isNewAgentType,
  ThirdPartyScriptsManager,
  useTraceIdVisible,
} from '@mygpt/app';
import RecommendList from '@mygpt/app/chat/components/RecommendList';
import { ReferenceSign, SessionListParam } from '@mygpt/types';
import { ChatContentBottomButton } from './ChatContentBottomButton';
import { ERROR_TYPE } from '@mygpt/types';
import classes from './chatContent.module.css';
import { PhoneList } from '@mygpt/app/chat/components/CustomizedComponents/PhoneList';
import { useMedia } from '@mygpt/app/utils/hooks';
import RecommendQuestionNodeList from '@mygpt/app/chat/components/RecommendQuestionNodeList';
import { findRegenerateQuestion } from '@mygpt/app/chat/utils/regenerateTarget';
import {
  getWidgetLoginUrl,
  listenWidgetLogin,
  openWidgetLoginPopup,
  useWidgetToken,
} from '@mygpt/app/chat/utils/widgetAuth';
import { clearWidgetToken, setWidgetToken } from '@mygpt/app/apis/widgetToken';

// 关闭「回答来源」时，从消息文本中彻底剥掉所有 <CITATION>/<citation> 标签。
// 之所以在这里一刀切而不是依赖 Markdown 内部的 referenceList 过滤，是因为 URL 类型的
// citation 在 referenceList 为空时会走 catalog-agent 路径豁免（保留），导致角标残留；
// 顺带也能盖住 LLM 输出格式异常导致的裸标签。
const stripCitations = (text: string | undefined | null): string => {
  if (!text) return '';
  return text.replace(/<\/?citation\b[^>]*?>/gi, '');
};

type SignatureMessagesDisplayType = 'all' | 'widget' | 'share' | 'desktop';

export interface I_SignatureMessages {
  checked: boolean;
  value: string;
  displayType: SignatureMessagesDisplayType;
  list: {
    iconIndex: number;
    value: string;
  }[];
}

interface ChatContentProps {
  id: string;
  item: SessionListParam;
  index: number;
  answerSource: boolean;
  pending?: string;
  chatMessages: SessionListParam[];
  sessionId: string;
  // 是否把工具条按钮显示在消息的右上方
  floatingActions?: boolean;
  signatureMessages?: I_SignatureMessages;
  pageSource: 'widget' | 'playground' | 'share';
  robotInfo?: APIRobotInfo;
  isPlaying?: boolean;
  ttsPause?: boolean;
  ttsLanguage?: string;
  onAudioControl?: (messageId: string | null, fullAnswerPlay?: boolean) => void;

  fetchQuestion: (value: string, options?: FetchQuestionOptions) => void;
  isLastMessage: (id: string) => boolean;
  regenerate: (value: string) => void;
  onWidgetLogin?: (onSuccess: () => void) => void;
  handleMessages: (obj: { rating: number; messageId: string }) => void;
  getReference: (ai_id: string, message_id: string) => Promise<ReferenceSign[]>;
  isDigitalHuman?: boolean;
  submitQuestion?: (value: string, faqID?: string) => void;
  // 初始推荐消息配置
  initialSuggestedMessages?: string[] | FaqNodeSearchByIds[];
  // 初始消息的数量，用于计算真正的对话消息数
  initMessagesCount?: number;
  widgetColor: {
    chatColor?: string;
    sendButtonColor?: string;
    userMessageColor?: string;
    userMessageTextColor?: string;
    botMessageColor?: string;
    botMessageTextColor?: string;
  };
}

export const ChatContent: React.FC<ChatContentProps> = (props) => {
  const { t } = useTranslation();
  const { setRobotInfo } = useBotMethods();
  const { setPageSource, ttsMuted } = useChatStore();
  const { isMobile } = useMedia();
  const traceIdVisible = useTraceIdVisible();

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(!copied);
      }, 4 * 1000);
    }
  }, [copied]);

  const {
    answerSource,
    chatMessages,
    fetchQuestion,
    getReference,
    handleMessages,
    id,
    index,
    initialSuggestedMessages,
    isLastMessage,
    isPlaying,
    item,
    onAudioControl,
    pageSource,
    pending,
    regenerate,
    robotInfo,
    sessionId,
    signatureMessages,
    floatingActions,
    ttsPause,
    ttsLanguage,
    isDigitalHuman,
    submitQuestion,
    initMessagesCount = 0,
    widgetColor,
  } = props;

  const isWidget = pageSource === 'widget';
  const enableTTS =
    sharedIntegrationsAvailable &&
    (robotInfo?.configs.enable_tts || (isWidget && robotInfo?.configs.enable_2d_digital_person));

  // 「再生成」要重发的用户提问；找不到就不显示按钮，避免按位置误发上一条回答
  const regenerateQuestion = item.error ? findRegenerateQuestion(chatMessages, index) : undefined;

  // 小部件内登录态（跨站 iframe 读不到 cookie，单独存 sessionStorage）
  const widgetToken = useWidgetToken();
  // 开启访问控制的 bot 在小部件里返回 401：未登录 → 引导登录；已登录仍 401 → 该账号无权访问
  const isWidgetLoginRequired =
    isWidget && item.error_type === ERROR_TYPE.PERMISSION_LIMIT && item.status === 401 && !widgetToken;
  const isWidgetAccessDenied =
    isWidget && item.error_type === ERROR_TYPE.PERMISSION_LIMIT && item.status === 401 && !!widgetToken;
  const stopListenLoginRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => stopListenLoginRef.current?.(), []);
  // 弹窗被浏览器 / iframe sandbox 拦截时，退化为提示 + 新标签页登录链接
  const [loginPopupBlocked, setLoginPopupBlocked] = useState(false);

  // 登录 / 退出按钮：紧凑尺寸；数字人模式下沿用推荐问题的白色毛玻璃风格，普通模式用主题色实心
  const widgetAuthButtonProps = isDigitalHuman
    ? {
        size: 'xs' as const,
        radius: 'md' as const,
        variant: 'white' as const,
        color: 'dark',
        className: 'bg-white/90 backdrop-blur-md shadow-lg hover:bg-white border-none',
      }
    : { size: 'xs' as const, radius: 'md' as const, variant: 'filled' as const, color: 'violet.5' };

  const startListenWidgetLogin = () => {
    stopListenLoginRef.current?.();
    stopListenLoginRef.current = listenWidgetLogin((token) => {
      stopListenLoginRef.current?.();
      stopListenLoginRef.current = undefined;
      setWidgetToken(token);
      // 登录成功后自动重发触发登录的那条提问
      if (regenerateQuestion) regenerate(regenerateQuestion);
    });
  };

  const handleWidgetLogin = () => {
    if (props.onWidgetLogin) {
      props.onWidgetLogin(() => {
        if (regenerateQuestion) regenerate(regenerateQuestion);
      });
      return;
    }
    startListenWidgetLogin();
    const popup = openWidgetLoginPopup();
    // window.open 返回 null = 被拦截；监听保持着，用户走新标签页链接登录同样能回传 token
    setLoginPopupBlocked(!popup);
  };

  const isInitialSuggestedMessage = useMemo(() => {
    return item.suggestedMessages && item.suggestedMessages.length > 0;
  }, [item.suggestedMessages]);

  // 判断是否显示"はじめにもどる"按钮
  const shouldShowReturnToInitialButton = useMemo(() => {
    // 基础条件：
    // 为了保持其他逻辑的兼容性，仍然计算realConversationMessages
    const realConversationMessages = chatMessages.slice(initMessagesCount);

    // 1. 当前消息是bot回答
    if (!item.isAnswer) {
      return false;
    }

    // 2. 流式输出过程中不显示按钮
    if (pending) {
      return false;
    }

    // 3. 非数字人模式
    if (isDigitalHuman) {
      return false;
    }

    // 4. 回答中没有继续返回子节点（表示FAQ路径到了末端）
    const hasChildNodes = item.faq_child_node && item.faq_child_node.length > 0;
    if (hasChildNodes) {
      return false;
    }

    // 5. 检查是否已经点击过"回到开头"
    // 获取当前消息在对话中的位置
    const currentIndex = chatMessages.findIndex((msg) => msg.messageId === item.messageId);
    if (currentIndex !== -1 && currentIndex < chatMessages.length - 1) {
      // 检查后面是否还有bot回答（说明已经点击过回到开头）
      const nextMessage = chatMessages[currentIndex + 1];
      if (nextMessage && nextMessage.isAnswer) {
        return false;
      }
    }

    // 6. 检查当前消息是否是"回到开头"后追加的推荐问题消息
    // 如果当前消息有suggestedMessages，且前一条消息也是bot回答，则不显示按钮
    if (item.suggestedMessages && item.suggestedMessages.length > 0) {
      if (currentIndex > 0) {
        const prevMessage = chatMessages[currentIndex - 1];
        if (prevMessage && prevMessage.isAnswer) {
          return false;
        }
      }
    }

    // 7. 检查是否是session中最后一个answer
    // 只在最后一个bot回答中显示按钮，避免历史消息中出现多余按钮
    const lastAnswerMessage = realConversationMessages.filter((msg) => msg.isAnswer).pop();
    const isLastAnswer = lastAnswerMessage && lastAnswerMessage.messageId === item.messageId;
    if (!isLastAnswer) {
      return false;
    }

    // 8. 检查最新的6条消息中是否包含初始消息
    // 如果最新的6条消息中包含初始消息，说明用户已经点击过"回到开头"按钮
    const latestMessages = chatMessages.slice(-6);
    const hasInitialMessagesInLatest = latestMessages.some((msg, index) => {
      return msg.isAnswer && msg.suggestedMessages && msg.suggestedMessages.length > 0;
    });

    if (hasInitialMessagesInLatest) {
      return false;
    }

    // 9. 存在初始推荐消息，且元素为QA对象，纯文字的推荐消息不展示按钮
    const hasInitialMessages =
      initialSuggestedMessages &&
      Array.isArray(initialSuggestedMessages) &&
      initialSuggestedMessages.length > 0 &&
      typeof initialSuggestedMessages[0] === 'object';

    if (!hasInitialMessages) {
      return false;
    }

    return true;
  }, [chatMessages, initialSuggestedMessages, item, isDigitalHuman, pageSource, initMessagesCount]);

  // 处理回到初始推荐问题的函数
  const handleReturnToInitial = useCallback(() => {
    if (typeof submitQuestion === 'function') {
      // 使用特殊标识符来触发显示初始推荐消息
      submitQuestion('__RETURN_TO_INITIAL__');
    }
  }, [submitQuestion]);

  useEffect(() => {
    setPageSource(pageSource || undefined);
  }, [pageSource]);

  useEffect(() => {
    robotInfo && setRobotInfo(robotInfo);
  }, [robotInfo]);

  const handleSignatureClick = ({ content, icon }: { icon: string; content: string }) => {
    signatureClickAction({ ai_id: id, session_id: sessionId, icon, content });
    const iconType = ['Email', 'Contact Number', 'Location', 'Fax Number', 'Link', 'Date', 'Time', 'Text'];
    isWidget &&
      ThirdPartyScriptsManager.safeMixpanelTrack('signature_message_clicked', {
        iconIndex: icon,
        iconType: iconType[+icon],
        value: content,
      });
  };

  const [ttsLoading, setTtsLoading] = useState<boolean>(false);
  // 分句播放音频管理
  const audioManager = useAudioManager({
    isDigitalHuman: isDigitalHuman,
  });
  // 整句播放音频管理
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioCache = useRef<string>(''); // 音频缓存

  const hasPausedRef = useRef<boolean>(false); // 暂停标记
  const isGettingTTSRef = useRef<boolean>(false); // tts队列请求状态标记

  useEffect(() => {
    // 如果正在获取tts/正在播放，监听到父元素有操作打断，则标记暂停
    if (ttsPause && (isGettingTTSRef.current || audioManager.isPlaying)) {
      hasPausedRef.current = true;
    }
  }, [ttsPause, audioManager.isPlaying]);

  useEffect(() => {
    // 监听到暂停标记为true时调用打断方法，停止tts请求、停止分段播放、停止整段播放
    hasPausedRef.current && handlePause();
  }, [hasPausedRef.current]);

  const handlePause = () => {
    // 停止整段播放
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // 停止分段播放
    if (audioManager.isPlaying) {
      audioManager.setAudioQueue([]);
      audioManager.stopAudio();
    }
  };

  // 点击消息气泡下方的播放/停止icon，整段播放/停止
  const handleTTSPlayOrPause = async (text: string) => {
    hasPausedRef.current = false;
    if (isPlaying) {
      handlePause();
      onAudioControl?.(null);
      return;
    }

    setTtsLoading(true);
    // 通知父组件开始播放当前消息
    onAudioControl?.(item.messageId || null, true);

    // 完整的text文本清理
    const cleanText = cleanMarkdownText(text);

    try {
      // 检查是否有缓存
      if (audioCache.current) {
        audioRef.current!.src = audioCache.current;
      } else {
        const audioBlob = await getTTS(cleanText);
        const audioUrl = window.URL.createObjectURL(audioBlob);
        // 缓存音频
        audioCache.current = audioUrl;
        audioRef.current!.src = audioUrl;
      }
      setTimeout(() => {
        audioRef.current?.play();
        setTtsLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to getTTS:', error);
      onAudioControl?.(null);
      setTtsLoading(false);
    }
  };

  useEffect(() => {
    if (!isPlaying && !audioRef.current?.paused) {
      handlePause();
    }
  }, [isPlaying]);

  const sentenceQueueRef = useRef<string[]>([]); // 分句队列
  const splitIndexRef = useRef<number>(0); // 分句索引
  const hasHadPendingRef = useRef<boolean>(false); // 跟踪该条消息是否曾经有过 pending 内容

  // 流式输出过程中，处理markdown文本，分句
  useEffect(() => {
    const shouldProcess =
      item.text &&
      chatMessages.at(-1)?.messageId === item.messageId &&
      !ttsMuted &&
      item.isAnswer &&
      enableTTS &&
      !hasPausedRef.current &&
      (pending || hasHadPendingRef.current); // 只有正在流式输出或曾经有过 pending 的消息才处理

    if (shouldProcess && pending) {
      // 标记该条消息曾经有过 pending 内容
      hasHadPendingRef.current = true;

      const [sentences, newIndex] = splitIntoSentences(cleanMarkdownText(item.text), splitIndexRef.current);
      splitIndexRef.current = newIndex;

      if (sentences.length > 0) {
        sentenceQueueRef.current = [...sentenceQueueRef.current, ...sentences.map((text) => text)];
      }
    }

    if (shouldProcess && !pending && hasHadPendingRef.current) {
      const remainingText = cleanMarkdownText(item.text).slice(splitIndexRef.current).trim();
      // 流式输出结束后，还有剩余内容，整体推进队列
      if (remainingText) {
        sentenceQueueRef.current = [...sentenceQueueRef.current, remainingText];
      }
    }
  }, [item.text, pending]);

  // 获取音频队列索引
  const getTTSIndexRef = useRef<number>(0);

  // 获取音频队列
  const getTTSQueue = async () => {
    console.log('getTTSQueue状态:', {
      sentenceQueueRef: sentenceQueueRef.current,
      getTTSIndexRef: getTTSIndexRef.current,
      isGettingTTSRef: isGettingTTSRef.current,
    });

    if (ttsMuted || hasPausedRef.current) return;
    // 如果请求的句子已经全部处理完毕，则恢复请求tts的状态标记
    if (getTTSIndexRef.current >= sentenceQueueRef.current.length) {
      isGettingTTSRef.current = false;
      return;
    }

    isGettingTTSRef.current = true;
    const sentence = sentenceQueueRef.current[getTTSIndexRef.current];
    console.log('请求tts', sentence);

    const isDnpAvatar =
      id &&
      [
        '5d82886e-a515-489e-a6ad-ad5099864a64',
        '28b48307-4701-4cf1-872a-cdadbafa6361',
        '6e96a3a8-3a29-48dd-8ef0-d2bc6744863c',
      ].includes(id);

    const audioBlob = await getTTS(
      sentence,
      ttsLanguage,
      isDnpAvatar && ttsLanguage === 'ja' ? 'ja-JP-DaichiNeural' : undefined
    );
    const audioUrl = URL.createObjectURL(audioBlob);

    audioManager.setAudioQueue((prev) => [...prev, audioUrl]);
    if (getTTSIndexRef.current < sentenceQueueRef.current.length) {
      getTTSIndexRef.current += 1;
    }
    getTTSQueue();
  };

  const getErrorText = (errorType: ERROR_TYPE | undefined, error: string) => {
    switch (errorType) {
      case ERROR_TYPE.PERMISSION_LIMIT:
        if (isWidgetLoginRequired) return t('errors.detail.widget_login_required');
        if (isWidgetAccessDenied) return t('errors.detail.widget_access_denied');
        return isAdmin ? error : t('errors.detail.chat_temporarily_unavailable');
      case ERROR_TYPE.API_ERROR:
        // 这种接口会返回错误信息，直接展示
        return error;
      case ERROR_TYPE.NETWORK_ERROR:
        return t('errors.network.description');
      case ERROR_TYPE.USER_CANCELED:
        return t('errors.user_canceled');
      default:
        return t('errors.unknown.description');
    }
  };

  // 分句队列发生变化，且tts请求队列没有在进行时，调用请求tts队列的方法，其它情况下getTTSQueue会自行递归调用
  // 仅发生在产生了第一个分句/tts请求结束了才产生了新的分句时
  useEffect(() => {
    if (sentenceQueueRef.current.length > 0 && !isGettingTTSRef.current && !hasPausedRef.current) {
      getTTSQueue();
    }
  }, [sentenceQueueRef.current.length]);

  // 流式输出过程中，通知父组件当前播放的音频id
  useEffect(() => {
    if (!item.messageId || !enableTTS) return;
    if (audioManager.isPlaying) {
      onAudioControl?.(audioManager.isPlaying ? item.messageId : null);
    } else if (!pending && !isGettingTTSRef.current) {
      onAudioControl?.(null);
    }
  }, [audioManager.isPlaying, pending]);

  if (!item.isAnswer) {
    return (
      <pre
        className={`text-[14px] whitespace-break-spaces ${isDigitalHuman && 'text-white'}`}
        style={{
          color: widgetColor?.userMessageTextColor ? widgetColor.userMessageTextColor : '#373851',
        }}
      >
        {item.text}
      </pre>
    );
  }

  return (
    <>
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        onEnded={() => {
          onAudioControl?.(null);
        }}
      />
      {isNewAgentType(robotInfo?.robot_type) ? (
        <AgentMessage
          content={answerSource ? item.text : stripCitations(item.text)}
          isDigitalHuman={isDigitalHuman}
          botMessageTextColor={widgetColor?.botMessageTextColor}
          accentColor={widgetColor?.chatColor}
          botMessageColor={widgetColor?.botMessageColor}
          isLastMessage={isLastMessage(item.messageId || '')}
          onAskUserReply={(answer) => fetchQuestion(answer)}
          botId={id}
          pending={!!(pending && chatMessages.at(-1)?.messageId === item.messageId)}
        />
      ) : (
        <Markdown
          className={`text-[14px]`}
          content={answerSource ? item.text : stripCitations(item.text)}
          footnotes={answerSource ? item.footnotes : []}
          referenceList={answerSource ? item.reference_list : []}
          referenceIndexes={answerSource ? item.reference_indexes : []}
          getReference={item.messageId && answerSource ? () => getReference(id, item.messageId!) : undefined}
          isDigitalHuman={isDigitalHuman}
          botMessageTextColor={widgetColor?.botMessageTextColor}
          accentColor={widgetColor?.chatColor}
          botMessageColor={widgetColor?.botMessageColor}
          suffixNode={
            pending && chatMessages.at(-1)?.messageId === item.messageId ? (
              <Loading color={isDigitalHuman ? '#ffffff' : undefined} />
            ) : undefined
          }
        />
      )}
      {item.prompt && <pre className="text-[14px] text-gray-600 whitespace-break-spaces mt-5">{item.prompt}</pre>}
      {item.source && !!item.source.length && !isDigitalHuman && (
        <>
          <Text c="gray.5" size={'xs'} fw={300} mt={12}>
            {t('completions.source')}:
          </Text>
          <Group mt={8} gap={12}>
            {item.source.map((item, index) => (
              <Button
                key={index}
                className={classes['btnTag']}
                size="sm"
                component="a"
                href={item}
                target={'_blank'}
                disabled={!checkIsUrl(item, true)}
              >
                {item}
              </Button>
            ))}
          </Group>
        </>
      )}
      {item.faq_child_node && !!item.faq_child_node.length && (
        <section className="my-3">
          <FaqChildNodeList
            aiId={id}
            list={item.faq_child_node}
            onClick={fetchQuestion}
            botMessageTextColor={widgetColor?.botMessageTextColor}
            isDigitalHuman={isDigitalHuman}
          />
        </section>
      )}
      {item.recommend_list && !!item.recommend_list.length && (
        <section className="pt-5 pb-2 mt-5 mb-2 rounded-xl border-t border-gray-200">
          <Text c="gray.4" size={'sm'} mt={5} mb={6}>
            {t('completions.related')}
          </Text>
          <RecommendList aiId={id} list={item.recommend_list} onClick={fetchQuestion} />
        </section>
      )}
      {item.clarification && item.clarification === 'phone' && <PhoneList onClick={fetchQuestion} />}

      {/* 渲染初始消息中的推荐问题 */}
      {isInitialSuggestedMessage && (
        <section className="mt-3">
          <RecommendQuestionNodeList
            list={item.suggestedMessages!!}
            onClick={submitQuestion!}
            botMessageTextColor={widgetColor?.botMessageTextColor}
            isDigitalHuman={isDigitalHuman}
          />
        </section>
      )}

      {item.error && (
        <div className="leading-7">
          <div className="flex gap-2 flex-col items-start prose dark:prose-invert text-[14px]">
            <p className={`mb-0 ${isDigitalHuman ? 'text-white' : ''}`}>{getErrorText(item.error_type, item.error)}</p>
            {isWidgetLoginRequired && (
              <Button {...widgetAuthButtonProps} onClick={handleWidgetLogin}>
                {t('signin')}
              </Button>
            )}
            {isWidgetLoginRequired && loginPopupBlocked && (
              <p className={`mb-0 text-xs ${isDigitalHuman ? 'text-white' : 'text-gray-500'}`}>
                {t('errors.detail.widget_login_popup_blocked')}{' '}
                {/* rel="opener" 是必须的：回调页靠 window.opener 把 token 传回当前 iframe */}
                <a
                  href={getWidgetLoginUrl()}
                  target="_blank"
                  rel="opener"
                  onClick={startListenWidgetLogin}
                  className={`underline ${isDigitalHuman ? 'text-white' : 'text-violet-600'}`}
                >
                  {t('widget.login_in_new_tab')}
                </a>
              </p>
            )}
            {isWidgetAccessDenied && (
              <Button {...widgetAuthButtonProps} onClick={clearWidgetToken}>
                {t('logout')}
              </Button>
            )}
            {item.error_type &&
              [ERROR_TYPE.API_ERROR, ERROR_TYPE.NETWORK_ERROR].includes(item.error_type) &&
              regenerateQuestion && (
                <Button
                  fullWidth={isMobile}
                  variant="outline"
                  color="violet.5"
                  size="xs"
                  leftSection={<SvgRefresh />}
                  onClick={() => {
                    regenerate(regenerateQuestion);
                  }}
                >
                  {t('completions.regenerate')}
                </Button>
              )}
          </div>
        </div>
      )}
      {item.isAnswer && !item.error && !item.userAborted && !isInitialSuggestedMessage && (
        <>
          {!isDigitalHuman && (!pending || index !== chatMessages.length - 1) && signatureMessages && (
            <section className="my-2">
              <Text size={'xs'} c="gray.4">
                {signatureMessages.value}
              </Text>
              {!!signatureMessages.list.length && (
                <ul className="bg-white rounded-lg px-2 py-3 mt-2 space-y-3">
                  {signatureMessages.list.map((item, index) => {
                    const ItemIcon = MESSAGE_SIGNIN_ICON_ARRAY[item.iconIndex];
                    return (
                      <li key={index}>
                        <Group wrap="nowrap" gap={5}>
                          <section className="p-1">
                            <ItemIcon />
                          </section>
                          <Text
                            c="gray.4"
                            size="sm"
                            lh={'1.2rem'}
                            className="cursor-pointer"
                            onClick={() => {
                              handleSignatureClick({ icon: item.iconIndex.toString(), content: item.value });
                            }}
                          >
                            {item.iconIndex === 0 && <a href={`mailto:${item.value}`}>{item.value}</a>}
                            {item.iconIndex === 1 && <a href={`tel:${item.value}`}>{item.value}</a>}
                            {item.iconIndex === 2 && (
                              <a href={`https://maps.google.com/maps?q=${item.value}`} target="_blank" rel="noreferrer">
                                {item.value}
                              </a>
                            )}
                            {item.iconIndex === 4 && (
                              <a href={`${item.value}`} target="_blank" rel="noreferrer">
                                {item.value}
                              </a>
                            )}
                            {![0, 1, 2, 4].includes(item.iconIndex) && item.value}
                          </Text>
                        </Group>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {!isDigitalHuman && shouldShowReturnToInitialButton && (
            <section className="my-3 pt-2">
              <UnstyledButton
                className="max-w-full bg-white border border-gray-200 rounded-lg py-2 px-3 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                onClick={handleReturnToInitial}
              >
                <Text size="xs" c="gray">
                  {t('return_to_initial')}
                </Text>
              </UnstyledButton>
            </section>
          )}

          {!(pending && chatMessages.at(-1)?.messageId === item.messageId) &&
            item.messageId &&
            !item.suggestedMessages && (
              <ChatContentBottomButton
                item={item}
                id={id}
                sessionId={sessionId}
                pageSource={pageSource}
                enableTTS={enableTTS || false}
                isPlaying={isPlaying}
                ttsLoading={ttsLoading}
                onTTSPlayOrPause={handleTTSPlayOrPause}
                copied={copied}
                setCopied={setCopied}
                handleMessages={handleMessages}
                floatingActions={floatingActions}
                isDigitalHuman={isDigitalHuman}
              />
            )}
          {traceIdVisible && item.isAnswer && item.traceId && !isDigitalHuman && (
            <div
              className="text-[10px] text-gray-400 opacity-70 text-right cursor-pointer select-none mt-1"
              onClick={() => navigator.clipboard.writeText(item.traceId!)}
            >
              trace_id: {item.traceId}
            </div>
          )}
        </>
      )}
      {!item.text && item.userAborted && (
        <Text size="sm" c="gray.4">
          {t('canceled')}
        </Text>
      )}
    </>
  );
};
